// File: ArticleVolunteeringVancouver.jsx

import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleVolunteeringVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "volunteering-made-easy-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Finding the Right Volunteer Opportunity",
    "section-2": "Popular Volunteer Organizations",
    "section-3": "Volunteering for International Students",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.govolunteer.ca/",
      label: "GoVolunteer",
    },
    {
      url: "https://volunteerbc.bc.ca/",
      label: "Volunteer BC",
    },
    {
      url: "https://vancouver.ca/",
      label: "City of Vancouver Official Website",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // We have five primary volunteer organizations with unique IDs:
  const locations = [
    {
      id: "food-bank",
      lat: 49.25130,
      lng: -122.94236,
      popupText: "Greater Vancouver Food Bank",
    },
    {
      id: "spca",
      lat: 49.26532,
      lng: -123.08204,
      popupText: "Vancouver SPCA (BC SPCA Vancouver Branch)",
    },
    {
      id: "habitat",
      lat: 49.21045,
      lng: -123.10720,
      popupText: "Habitat for Humanity Greater Vancouver",
    },
    {
      id: "stanley-park-ecology",
      lat: 49.30202,
      lng: -123.14163,
      popupText: "Stanley Park Ecology Society",
    },
    {
      id: "vancouver-aquarium",
      lat: 49.29636,
      lng: -123.13035,
      popupText: "Vancouver Aquarium (Ocean Wise)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>10 Smart Ways to Volunteer in Vancouver (And Make a Real Difference)</title>
        <meta
          name="description"
          content="A thorough guide to volunteering in Vancouver, complete with practical tips, local organizations, and actionable advice."
        />
        <meta
          name="keywords"
          content="Vancouver, volunteering, community service, food bank, spca, habitat for humanity"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/volunteering-made-easy-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="10 Smart Ways to Volunteer in Vancouver (And Make a Real Difference)"
        />
        <meta
          property="og:description"
          content="A detailed guide to finding the right volunteering opportunities in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/volunteering-made-easy-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/volunteering-made-easy-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="10 Smart Ways to Volunteer in Vancouver (And Make a Real Difference)"
        />
        <meta
          name="twitter:description"
          content="Learn practical steps, discover top organizations, and make a tangible impact in Vancouver."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/volunteering-made-easy-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt={ref_data?.["image-desc"] || "Volunteering in Vancouver"}
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction: Why Volunteering in Vancouver Matters</h2>
            <p>
              Vancouver, known for its stunning landscapes and vibrant cultural
              scene, also offers countless opportunities to make a positive
              impact. Volunteering is a fantastic way to meet people, contribute
              to the community, and gain valuable experience. In this guide,
              we’ll explore straightforward ways to get involved—complete with
              practical tips, places to sign up, and key resources.
            </p>

            {/* SECTION: 1 */}
            <h2 id="section-1">Finding the Right Volunteer Opportunity</h2>
            <p>
              <strong>Assess Your Interests and Skills:</strong> Start by
              identifying your passions and capabilities. Decide if you can
              volunteer on weekends, weekdays, or evenings, and look for
              organizations that match your goals—personal, professional, or
              both.
            </p>
            <p>
              <strong>Check Local Directories and Platforms:</strong> Local
              volunteer portals like{" "}
              <em>GoVolunteer</em> or <em>Volunteer BC</em> list hundreds of
              opportunities across various sectors. You can also browse
              community bulletin boards and social media groups where Vancouver
              organizations frequently post volunteer calls.
            </p>

            {/* SECTION: 2 */}
            <h2 id="section-2">Popular Volunteer Organizations & Locations</h2>
            <p>
              From food security initiatives to animal welfare and environmental
              projects, here are five notable organizations to consider.
            </p>

            {/* Food Bank */}
            <p
              ref={(el) => setSpotRef("food-bank", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Greater Vancouver Food Bank </strong>
              <Marker
                coordY={49.25130}
                coordX={-122.94236}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <em>Address & Hours:</em> 8345 Winston St, Burnaby, BC V5A 2H3. 
              Open weekdays from 9:00 AM to 3:00 PM.  
              <br />
              <em>What They Do:</em> Sort, pack, and distribute food to those in
              need.  
              <br />
              <em>Why Volunteer Here:</em> A direct, high-impact environment where
              your work reaches individuals and families struggling with food
              insecurity.
            </p>

            {/* SPCA */}
            <p
              ref={(el) => setSpotRef("spca", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vancouver SPCA (BC SPCA Vancouver Branch)</strong>{" "}
              <Marker
                coordY={49.26532}
                coordX={-123.08204}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <em>Address & Hours:</em> 1205 E 7th Ave, Vancouver, BC V5T 1R1. 
              Open daily from 10:30 AM to 5:30 PM.  
              <br />
              <em>What They Do:</em> Rescue and shelter animals, advocate for
              animal welfare, and offer various volunteer roles from dog-walking
              to administrative support.  
              <br />
              <em>Why Volunteer Here:</em> Perfect for animal lovers eager to
              make a difference in pet welfare.
            </p>

            {/* Habitat for Humanity */}
            <p
              ref={(el) => setSpotRef("habitat", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Habitat for Humanity Greater Vancouver</strong>{" "}
              <Marker
                coordY={49.21045}
                coordX={-123.10720}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <em>Address & Hours (Admin Office):</em> 69 W 69th Ave, Vancouver,
              BC V5X 2W6. Typically open Monday to Friday, 9:00 AM to 5:00 PM.  
              <br />
              <em>What They Do:</em> Build affordable housing and run ReStores to
              fund projects. Volunteers can assist in construction or work in
              ReStores.  
              <br />
              <em>Why Volunteer Here:</em> Gain practical home-building skills
              while supporting local families in need of affordable housing.
            </p>

            {/* Stanley Park Ecology Society */}
            <p
              ref={(el) => setSpotRef("stanley-park-ecology", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Stanley Park Ecology Society</strong>{" "}
              <Marker
                coordY={49.30202}
                coordX={-123.14163}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <em>Address & Hours:</em> 610 Pipeline Rd, Vancouver, BC V6G 3E2.
              Office hours are 9:00 AM to 5:00 PM on weekdays. Weekend volunteer
              events may also be available.  
              <br />
              <em>What They Do:</em> Focus on environmental conservation, habitat
              restoration, and wildlife monitoring within Stanley Park.  
              <br />
              <em>Why Volunteer Here:</em> Great for nature enthusiasts who want
              to protect and learn about local ecosystems.
            </p>

            {/* Vancouver Aquarium */}
            <p
              ref={(el) => setSpotRef("vancouver-aquarium", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vancouver Aquarium (Ocean Wise Conservation Association)</strong>{" "}
              <Marker
                coordY={49.29636}
                coordX={-123.13035}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              <br />
              <em>Address & Hours:</em> 845 Avison Way, Vancouver, BC V6G 3E2.
              Generally open daily 10:00 AM to 5:00 PM (hours may vary).  
              <br />
              <em>What They Do:</em> Marine conservation, animal rehabilitation,
              and education programs. Volunteers assist with guest services,
              animal care, and event support.  
              <br />
              <em>Why Volunteer Here:</em> Ideal for those passionate about
              marine life and contributing to ocean conservation efforts.
            </p>

            {/* SECTION: 3 (Volunteering for International Students) */}
            <h2 id="section-3">Volunteering for International Students</h2>
            <p>
              <strong>Eligibility:</strong> Most Canadian study permits allow
              full-time international students to volunteer, but always confirm
              the details on official Immigration, Refugees and Citizenship
              Canada (IRCC) resources.  
              <br />
              <strong>Language Support:</strong> Some organizations offer roles
              or training specifically designed for volunteers who are learning
              English.  
              <br />
              <strong>Cultural Exchange:</strong> Volunteering can help
              international students integrate into the local community, improve
              language skills, and form valuable friendships.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              <strong>Volunteer Visas:</strong> International visitors must
              verify their eligibility before volunteering to avoid any visa
              issues.  
              <br />
              <strong>Background Checks:</strong> Many organizations, especially
              those dealing with children or vulnerable adults, require criminal
              record checks.  
              <br />
              <strong>Safety & Orientation:</strong> Always attend training and
              follow official safety procedures to protect yourself and
              beneficiaries.  
              <br />
              <strong>Respect Community Protocols:</strong> If volunteering on
              Indigenous lands or culturally sensitive areas, ensure you follow
              local guidelines and obtain any necessary permissions.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need prior experience to volunteer?</h3>
              <p>
                Many organizations offer on-the-job training or orientations.
                Enthusiasm and willingness to learn are often more important
                than formal experience.
              </p>

              <h3>2. Can I volunteer at multiple places simultaneously?</h3>
              <p>
                Yes, as long as you can manage your schedule and meet each
                organization's time commitments. Make sure to avoid overlap or
                burnout.
              </p>

              <h3>3. Are there age restrictions?</h3>
              <p>
                Some volunteer roles require you to be at least 16 or 18, while
                others may allow younger volunteers with parental consent. Check
                with each organization for specifics.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Volunteering in Vancouver is not just about giving back—it’s a
              rewarding experience that helps you grow your skills, expand your
              network, and contribute to vibrant local communities. Whether you
              choose to support vital services at the Greater Vancouver Food
              Bank or protect wildlife habitats with the Stanley Park Ecology
              Society, your efforts can have a tangible, positive impact. Take
              the next step by exploring local volunteer hubs or directly
              reaching out to one of these fantastic organizations.
            </p>

            <p>
              <em>
                Disclaimer: Always verify the latest details from official
                sources and contact local authorities or the organization
                directly before finalizing any volunteer plans.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}